import { useContext, useEffect, useRef, useState } from "react";
import Cancelimg from "../../assets/img/cancel-01.svg";
import docIcon from "../../assets/img/docIcon02.svg";
import MissingIcon from "../../assets/img/zondicons.svg";
import Userimg from "../../assets/img/gridicons_user2.svg";
import useTranslator from "../../hooks/useTranslator";
import DocumentAnalysisLoadingPopup from "../../components/Onboarding/DocumentAnalysisLoading";
import TenantContext from "../../context/tenant/TenantContext";
import {
  getDocumentsFromEmploymentType,
  haveValue,
  toast,
  getRequiredPayslipPeriods,
} from "../../helper/common";
import { docsMap } from "../../Utils/skorLiteDocumentMap";
import { useNavigate } from "react-router-dom";
import docsData from "../../Utils/docsData";
import ContinueAddingPopup from "./ContinueAddingPopup";
import { trackClarityEvent } from "../../Utils/clarityReady";
import constants from "../../Utils/constants";
import DuplicateDocModal from "../../components/Onboarding/DuplicateDocModal";
import AuthContext from "../../context/auth/AuthContext";

const PAYSLIP_PERIOD_KEYS = [
  constants.firstMonthPayslip,
  constants.secondMonthPayslip,
  constants.thirdMonthPayslip,
];

const MissingDocument = ({
  documentAnalysisResponse,
  setDocumentAnalysisResponse,
  formHandler,
  user,
  selectedOnboardingUsers,
  setSelectedOnboardingUsers,
  updateUserProfile,
  isGuarantor,
  isEditMode,
}) => {
  const { _t } = useTranslator();
  const navigate = useNavigate();
  const [docAnalysisLoading, setDocAnalysisLoading] = useState(true);
  const { checkDocumentAnalysisStatus, upsertSelectedOnboardingUsers } =
    useContext(TenantContext);
  const { loadUserTenant } = useContext(AuthContext);
  const [missingDocuments, setMissingDocuments] = useState([]);
  const [otherUsers, setOtherUsers] = useState([]);
  const [submittingLoading, setSubmittingLoading] = useState(false);
  const [showSkipStepPopup, setShowSkipStepPopup] = useState(false);
  const [skipStepLoading, setSkipStepLoading] = useState(false);
  const [hasDuplicates, setHasDuplicates] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [duplicateDocsByProfile, setDuplicateDocsByProfile] = useState({});

  const intervalRef = useRef(null);
  const timeoutRef = useRef(null);

  const checkDocumentAnalysis = async () => {
    const tenantId = user?._id;
    if (!tenantId) return;

    let popupShown = false;

    timeoutRef.current = setTimeout(() => {
      if (!popupShown) {
        setShowSkipStepPopup(true);
        popupShown = true;
      }
    }, 20000);

    intervalRef.current = setInterval(async () => {
      try {
        const res = await checkDocumentAnalysisStatus(tenantId);

        if (res?.success_res?.status === "done") {
          clearTimeout(timeoutRef.current);
          clearInterval(intervalRef.current);
          setDocumentAnalysisResponse(res?.success_res?.document_analysis);
          setDocAnalysisLoading(false);
          return;
        }

        if (res?.success_res?.status === "failed") {
          clearTimeout(timeoutRef.current);
          clearInterval(intervalRef.current);
          setDocAnalysisLoading(false);
          toast.error(
            _t(
              "documentAnalysisFailedPleaseReUpload",
              "Text used in onboarding flow"
            )
          );
          return;
        }

        if (res?.success_res?.status === "processing") {
          setDocAnalysisLoading(true);
          return;
        }

        console.warn("Unexpected status or missing data", res);
        clearTimeout(timeoutRef.current);
        clearInterval(intervalRef.current);
      } catch (error) {
        console.error("Error checking document analysis:", error);
        clearTimeout(timeoutRef.current);
        clearInterval(intervalRef.current);
        setDocAnalysisLoading(false);
      }
    }, 5000);
  };

  useEffect(() => {
    checkDocumentAnalysis();
  }, []);

  useEffect(() => {
    console.log(hasDuplicates, user?.tenant_extra_field_id, "hasDuplicates");
    if (hasDuplicates && user?.tenant_extra_field_id?.duplicate_doc_modal) {
      setShowDuplicateModal(true);
    }
  }, [hasDuplicates, user]);
  useEffect(() => {
    if (Object?.entries?.(duplicateDocsByProfile)?.length == 0) {
      setHasDuplicates(false);
      setShowDuplicateModal(false);
    }
  }, [duplicateDocsByProfile]);

  useEffect(() => {
    if (haveValue(documentAnalysisResponse)) {
      loadUserTenant("tenant");
      let uploader = {};
      let otherUsers = [];
      const docResponse =
        documentAnalysisResponse?.document_analysis?.profileReports ??
        documentAnalysisResponse?.profileReports ??
        [];

      // Finding uploader
      for (var dataItem of docResponse) {
        if (dataItem?.data?.is_uploader) {
          uploader = dataItem;
        } else {
          otherUsers.push(dataItem);
        }
      }
      const profiles = [uploader, ...otherUsers];

      let globalHasDuplicates = false;
      const duplicatesMap = {};

      profiles.forEach((profile) => {
        const profileId = profile?.data?.owner || "unknown";

        const duplicateDocs = [];
        const seenDocNames = new Set(); // 👈 key change

        (profile?.report?.allDocuments || []).forEach((doc) => {
          if (doc?.isDuplicate) {
            globalHasDuplicates = true;

            const localizedName =
              doc?.documentNameTranslated?.[
                localStorage.getItem("i18nextLng") == "es" ? "es" : "en"
              ] || doc?.documentName;

            // 👇 only add if not already seen
            if (!seenDocNames.has(localizedName)) {
              seenDocNames.add(localizedName);

              duplicateDocs.push({
                name: localizedName,
                documentId: doc?.documentId,
                // you can skip instanceId now if not needed
                duplicateOf: doc?.duplicateOfDocumentId,
                bucketName: doc?.bucketName,
              });
            }
          }
        });

        if (duplicateDocs.length > 0) {
          duplicatesMap[profileId] = duplicateDocs;
        }
      });

      setHasDuplicates(globalHasDuplicates);
      setDuplicateDocsByProfile(duplicatesMap);

      // Finding missing documents
      let userAllDocuments = getDocumentsFromEmploymentType(user, true);

      // protected housing logic starts

      const selectedProtectedDocs =
        user?.tenant_extra_field_id?.[constants.public_housing_fileFrom] ?? [];
      const hasNoSpecialCircumstances = selectedProtectedDocs.includes(
        constants.no_special_circumstances
      );

      const residencyDoc = constants.certificate_of_residency_registration;
      const selectedBaseProtectedDocs = selectedProtectedDocs.filter(
        (doc) =>
          doc &&
          doc !== constants.no_special_circumstances &&
          doc !== residencyDoc
      );

      const effectiveProtectedDocs =
        selectedBaseProtectedDocs.length > 0
          ? [...selectedBaseProtectedDocs, residencyDoc]
          : hasNoSpecialCircumstances
            ? [residencyDoc]
            : [];

      const protectedHousingDocs = Object.keys(
        docsData.PROTECTED_HOUSING
      ).filter((key) => key !== constants.no_special_circumstances);

      let docsToRemove = protectedHousingDocs.filter(
        (key) => !effectiveProtectedDocs.includes(key)
      );

      docsToRemove.push(constants.no_special_circumstances);

      const otherDocsOfUploader = [];
      (uploader?.report?.otherDocuments ?? []).forEach((docEntry) => {
        const docType = docEntry?.data?.name;
        otherDocsOfUploader.push(docType);
      });

      userAllDocuments = Object.keys(userAllDocuments)
        .filter(
          (label) =>
            !docsToRemove.includes(label) &&
            !otherDocsOfUploader.includes(label)
        )
        .reduce((obj, label) => {
          obj[label] = userAllDocuments[label];
          return obj;
        }, {});

      effectiveProtectedDocs.forEach((docType) => {
        if (!userAllDocuments[docType]) {
          userAllDocuments[docType] = docsData.PROTECTED_HOUSING[docType];
        }
      });

      // protected housing logic ends

      const matchedDocuments = uploader?.report?.matchedDocuments ?? [];
      //   #NOTE: the matched doc has fields in them, by which we can identify the year and month of it.
      // period_year and period_month, these are numbers. example: period_year: 2026, period_month: 1

      const matchedDocumentsMapped = new Map();
      for (var doc of matchedDocuments) {
        if (doc?.documentName) {
          const mapName = docsMap?.[doc?.documentName] ?? doc?.documentName;
          matchedDocumentsMapped.set(mapName, doc);
        }
      }

      const expectedKeys = Object.keys(userAllDocuments);
      let matchedKeys = Array.from(matchedDocumentsMapped.keys());

      const isThreePayslipRequired =
        user?.employment_status === constants.employed ||
        user?.employment_status === constants.public_servant;

      if (isThreePayslipRequired) {
        const requiredPayslipPeriods = getRequiredPayslipPeriods();
        const matchedPeriodIndexes = new Set();

        for (const doc of matchedDocuments) {
          if (doc?.documentName?.toLowerCase()?.includes("payslip")) {
            const payslipPeriodYear = doc?.period_year;
            const payslipPeriodMonth = doc?.period_month;

            if (
              payslipPeriodYear &&
              payslipPeriodMonth &&
              requiredPayslipPeriods.length > 0
            ) {
              const matchedPeriodIndex = requiredPayslipPeriods.findIndex(
                (period) =>
                  period.period_year === payslipPeriodYear &&
                  period.period_month === payslipPeriodMonth
              );

              if (
                matchedPeriodIndex >= 0 &&
                !matchedPeriodIndexes.has(matchedPeriodIndex)
              ) {
                matchedPeriodIndexes.add(matchedPeriodIndex);
                const payslipKey = PAYSLIP_PERIOD_KEYS[matchedPeriodIndex];
                if (!matchedKeys.includes(payslipKey)) {
                  matchedKeys.push(payslipKey);
                }
              }
            }
          }
        }

        const existingPayslipKeys = PAYSLIP_PERIOD_KEYS.filter((key) =>
          matchedKeys.includes(key)
        );

        for (const existingKey of existingPayslipKeys) {
          const index = PAYSLIP_PERIOD_KEYS.indexOf(existingKey);
          if (index >= 0 && !matchedPeriodIndexes.has(index)) {
            matchedPeriodIndexes.add(index);
          }
        }
      }

      const missingKeys = expectedKeys.filter(
        (key) => !matchedKeys.includes(key)
      );

      if (missingKeys.length === 0 && otherUsers.length === 0) {
        handleSkipStep();
      }

      setMissingDocuments(missingKeys);
      setOtherUsers(otherUsers);
    }
  }, [documentAnalysisResponse]);

  const handleSelection = (owner, selectedRole) => {
    setSelectedOnboardingUsers((prevUsers) => {
      const existing = prevUsers.find((user) => user.name === owner);

      if (existing && existing.type === selectedRole) {
        return prevUsers.filter((user) => user.name !== owner);
      }

      if (existing) {
        return prevUsers.map((user) =>
          user.name === owner ? { ...user, type: selectedRole } : user
        );
      }

      return [...prevUsers, { name: owner, type: selectedRole }];
    });
  };

  const handleNextButton = async () => {
    try {
      setSubmittingLoading(true);
      const payload = {
        tenant_id: user?._id,
        users: selectedOnboardingUsers,
      };
      await upsertSelectedOnboardingUsers(payload);
      if (isEditMode) {
        navigate("/home");
      } else {
        const role = localStorage.getItem("usertype");
        const nextStep = user?.skip_steps?.includes(
          constants.SKIP_STEPS_ENUM.bank_onboarding
        )
          ? 7
          : 6;
        await updateUserProfile({ step: nextStep, role });
        trackClarityEvent("tenant_missing_document_step_ended");
        setTimeout(() => {
          trackClarityEvent(
            nextStep === 7
              ? "tenant_guarantor_step_started"
              : "tenant_bank_connect_step_started"
          );
        }, constants.CLARITY_DELAY);
      }
      // formHandler.next();
    } catch (error) {
      console.log("Error in submitting ", error);
    } finally {
      setSubmittingLoading(false);
    }
  };

  const handleSkipStep = async () => {
    try {
      setSkipStepLoading(true);
      clearTimeout(timeoutRef.current);
      clearInterval(intervalRef.current);
      const role = localStorage.getItem("usertype");
      const nextStep = user?.skip_steps?.includes(
        constants.SKIP_STEPS_ENUM.bank_onboarding
      )
        ? 7
        : 6;
      await updateUserProfile({ step: nextStep, role });
      trackClarityEvent("tenant_missing_document_step_ended");
      setTimeout(() => {
        trackClarityEvent(
          nextStep === 7
            ? "tenant_guarantor_step_started"
            : "tenant_bank_connect_step_started"
        );
      }, constants.CLARITY_DELAY);
    } catch (error) {
      console.log("Error skipping step", error);
    } finally {
      setSkipStepLoading(false);
    }
  };

  return (
    <>
      <div
        className={`approvedModal popup-overlay fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 wrapper-modal modal-fixed-screen ${
          isGuarantor ? "guarantorBg" : ""
        }`}
      >
        <div
          className="popup-content2 popup-content position-relative bg-white required-doc-popup missing-doc-popup"
          onClick={isGuarantor ? (e) => e.stopPropagation() : null}
        >
          <div className="row">
            <div className="col-12 d-flex gap-4 align-items-center justify-content-between missing-head-section">
              {missingDocuments.length > 0 && (
                <h4>
                  {_t(
                    "missingDocumentsAreYouSureYouWantToContinue",
                    "Text used in missing documents popup at onboarding flow"
                  )}
                </h4>
              )}
              {(isGuarantor || isEditMode) && (
                <div
                  className="cursor-pointer"
                  onClick={() => navigate("/home")}
                >
                  <img
                    src={Cancelimg}
                    alt="icon"
                    className="canceicon topRightIcon"
                  />
                </div>
              )}
            </div>
          </div>
          <div className="row" style={{ minHeight: "inherit" }}>
            {missingDocuments.length > 0 && (
              <div
                className={`col-12 d-flex gap-4 align-items-start justify-content-between heading-section ${
                  otherUsers.length === 0 && isGuarantor ? "headingSection" : ""
                }`}
                style={{ minHeight: "inherit" }}
              >
                <div className="row w-100 docListWrapper">
                  {missingDocuments.map((doc, index) => (
                    <div className="col-md-4 col-12" key={index}>
                      <div className="annual-report annualReport d-flex align-items-center justify-content-between gap-2">
                        <div className="d-flex align-items-center box-mar w-100 gap-2">
                          <div className="d-flex align-items-start gap-2">
                            <img
                              className="docoment-img2"
                              src={docIcon}
                              alt="icon"
                            />
                            <div>
                              <p className="staticdata_div">
                                {_t(doc, "", "fixed")}
                              </p>
                            </div>
                          </div>
                          <div className="annual-report annualReport missingreport d-flex align-items-center gap-2 border-0 p-0 m-0">
                            <img
                              className="docoment-img2"
                              src={MissingIcon}
                              alt="icon"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {otherUsers.length > 0 &&
              !isGuarantor &&
              user?.gurantor_for?.length === 0 && (
                <div className="row custom-pb-2 no-gutter-x foundOtherWrapper">
                  <div className="col-12">
                    <div className="identify-heading">
                      <h5>
                        {_t(
                          "weHaveFoundOthersPleaseIdentifyThemAs",
                          "Text used in missing documents popup at onboarding flow"
                        )}
                      </h5>
                      <p>
                        {_t(
                          "ifYouContinueWithoutIdentifyingTheirRolesTheirDocumentsWillBeDiscarded",
                          "Text used in missing documents popup at onboarding flow"
                        )}
                      </p>
                    </div>
                    <div className="guarantor-wrapper">
                      {otherUsers.map((item, index) => {
                        const owner = item?.data?.owner;
                        const selectedType =
                          selectedOnboardingUsers.find((u) => u?.name === owner)
                            ?.type || "";

                        return (
                          <div
                            className="userimg-btn-style-wrapper"
                            key={index}
                          >
                            <div className="userimg-btn-style d-flex justify-content-between gap-4">
                              <div className="d-flex align-items-center gap-2 box-img-style">
                                <div>
                                  <img src={Userimg} alt="img" />
                                </div>
                                <div>
                                  <p>{owner}</p>
                                </div>
                              </div>
                              <div className="d-flex gap-3 selectorbtn selectorbtndesktop">
                                <button
                                  type="button"
                                  className={`btnNext btnnextpre ${
                                    selectedType === "sharer" ? "selected" : ""
                                  }`}
                                  onClick={() =>
                                    handleSelection(owner, "sharer")
                                  }
                                >
                                  {_t("sharingRentOrFlat")}
                                </button>
                                <button
                                  type="button"
                                  className={`btnNext btnnextpre ${
                                    selectedType === "guarantor"
                                      ? "selected"
                                      : ""
                                  }`}
                                  onClick={() =>
                                    handleSelection(owner, "guarantor")
                                  }
                                >
                                  {_t("guarantor")}
                                </button>
                              </div>
                            </div>
                            <div
                              className="report-list-pdf flex-wrap d-flex align-items-center gap-3"
                              style={{
                                borderBottom:
                                  otherUsers?.length - 1 === index
                                    ? "none"
                                    : "",
                              }}
                            >
                              {(item?.report?.allDocuments ?? []).map(
                                (item, index) => (
                                  <div
                                    key={index}
                                    className="d-flex align-items-start gap-2 report-list-pdf-item"
                                  >
                                    <div className="d-flex">
                                      <img src={docIcon} alt="document icon" />
                                    </div>
                                    <div>
                                      <h6
                                        className="m-0 hover-underline cursor-pointer"
                                        onClick={() => {
                                          window.open(
                                            item?.data?.url,
                                            "_blank"
                                          );
                                        }}
                                      >
                                        {haveValue(
                                          docsMap?.[item?.documentName]
                                        )
                                          ? _t(
                                              docsMap?.[item?.documentName],
                                              "",
                                              "fixed"
                                            )
                                          : item?.documentName}
                                      </h6>
                                    </div>
                                  </div>
                                )
                              )}
                            </div>
                            <div className="d-flex gap-3 selectorbtn selectorbtnmobile">
                              <button
                                type="button"
                                className={`btnNext btnnextpre ${
                                  selectedType === "sharer" ? "selected" : ""
                                }`}
                                onClick={() => handleSelection(owner, "sharer")}
                              >
                                {_t("sharingRentOrFlat")}
                              </button>
                              <button
                                type="button"
                                className={`btnNext btnnextpre ${
                                  selectedType === "guarantor" ? "selected" : ""
                                }`}
                                onClick={() =>
                                  handleSelection(owner, "guarantor")
                                }
                              >
                                {_t("guarantor")}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            <div className="d-flex align-items-center buttonWrapper justify-content-center gap-3 custom-mt-2">
              <button
                type="button"
                className="btnNext btnnextpre missingDocumentsButton"
                onClick={() => {
                  formHandler.prev();
                  trackClarityEvent("tenant_reupload_document_step");
                }}
              >
                {_t("goBackAndReUpload")}
              </button>
              <button
                type="button"
                onClick={handleNextButton}
                className="btnNext missingDocumentsButton greenHover"
                disabled={submittingLoading}
              >
                {isEditMode ? _t("submit") : _t("continue")}
              </button>
            </div>
          </div>
          <DocumentAnalysisLoadingPopup
            isGuarantor={isGuarantor}
            showPopup={docAnalysisLoading}
          />
          {showSkipStepPopup && (
            <ContinueAddingPopup
              setShowSkipStepPopup={setShowSkipStepPopup}
              handleSkipStep={handleSkipStep}
              skipStepLoading={skipStepLoading}
            />
          )}
          {showDuplicateModal && (
            <DuplicateDocModal
              showDuplicateModal={showDuplicateModal}
              setShowDuplicateModal={setShowDuplicateModal}
              duplicateDocsByProfile={duplicateDocsByProfile}
              setDuplicateDocsByProfile={setDuplicateDocsByProfile}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default MissingDocument;
